/**
 * Authentication Context Provider
 * 
 * Provides authentication state management across the TabPicker application.
 * Handles user authentication, session persistence, login/logout operations,
 * and integrates with Google OAuth flow.
 * 
 * Features:
 * - Persistent authentication state via API session validation
 * - Google OAuth integration with secure logout flow
 * - Forced re-authentication after logout
 * - Loading states during authentication checks
 * - Error handling for authentication operations
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useGetApiAuthMe, usePostApiAuthLogout } from '../api/generated';
import { setSentryUserContext } from '../utils/sentry';

/**
 * User Account Information
 * 
 * Represents an authenticated user in the TabPicker application.
 */
interface User {
  /** Unique user ID from database */
  id: number;
  /** User's email address from Google OAuth */
  email: string;
  /** User's display name from Google OAuth */
  name: string;
  /** Google account ID for OAuth integration */
  googleId?: string;
}

/**
 * Authentication Context Type Definition
 * 
 * Defines the shape of the authentication context provided to components.
 */
interface AuthContextType {
  /** Currently authenticated user or null if not logged in */
  user: User | null;
  /** Whether authentication check is in progress */
  loading: boolean;
  /** Function to log in a user with provided user data */
  login: (user: User) => void;
  /** Function to log out the current user */
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Component
 * 
 * Provides authentication context to child components. Manages user state,
 * handles session persistence, and provides login/logout functionality.
 * Uses React Query for efficient data fetching and caching.
 * 
 * @param props - Component props
 * @param props.children - Child components that need access to auth context
 * @returns JSX element providing authentication context
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  // Use the generated hook for authentication check
  const { 
    data: authData, 
    isLoading: loading, 
    error: authError,
    isSuccess
  } = useGetApiAuthMe({
    query: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  });

  // Use the generated logout mutation
  const logoutMutation = usePostApiAuthLogout();

  // Update user state when auth data changes
  useEffect(() => {
    if (isSuccess && authData?.user) {
      setUser(authData.user);
      setSentryUserContext({
        id: authData.user.id.toString(),
        email: authData.user.email,
        name: authData.user.name,
      });
    } else if (authError) {
      setUser(null);
      setSentryUserContext(null);
    }
  }, [authData, isSuccess, authError]);

  /**
   * Login User
   * 
   * Sets the authenticated user in local state. Called after successful
   * OAuth flow completion.
   * 
   * @param userData - Authenticated user information
   */
  const login = (userData: User) => {
    setUser(userData);
    // Set Sentry user context for error tracking
    setSentryUserContext({
      id: userData.id.toString(),
      email: userData.email,
      name: userData.name,
    });
  };

  /**
   * Logout User
   * 
   * Clears user session both locally and on server using React Query mutation.
   * Sets forceReauth flag to require account selection on next login attempt.
   */
  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
      setUser(null);
      setSentryUserContext(null);
      // Set flag to force re-authentication on next login
      try {
        sessionStorage.setItem('forceReauth', 'true');
      } catch (storageError) {
        console.warn('Failed to set forceReauth flag:', storageError);
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local state even if API call fails
      setUser(null);
      setSentryUserContext(null);
      try {
        sessionStorage.setItem('forceReauth', 'true');
      } catch (storageError) {
        console.warn('Failed to set forceReauth flag:', storageError);
      }
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Authentication Hook
 * 
 * Custom hook to access authentication context. Must be used within
 * an AuthProvider component tree.
 * 
 * @returns Authentication context containing user state and auth functions
 * @throws Error if used outside of AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}